import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Inject, OnInit } from '@angular/core';
import { DomSanitizer }      from '@angular/platform-browser';
import { NgbModal }          from '@ng-bootstrap/ng-bootstrap';
import { TranslateService }  from '@ngx-translate/core';
import { UIRouter }          from '@uirouter/core';

import { ApiService }                  from '../api/api.service';
import { BusinessIntelligenceService } from '../business-intelligence/business-intelligence.service';
import { CompanyService }              from '../companies/company.service';
import { ConfirmService }              from '../messages/confirm/confirm.service';
import { MessagesService }             from '../messages/messages.service';
import { MyobService }                 from '../myob/myob.service';
import { PluralizePipe }               from '../shared/pipes/pluralize.pipe';
import { QuickbooksService }           from '../quickbooks/quickbooks.service';
import { SessionService }              from '../sessions/session.service';
import { SessionWarningModel }         from '../sessions/models/session.warning';
import { SidebarService }              from '../sidebar/sidebar.service';
import { XeroService }                 from '../xero/xero.service';

import userProfileIcon from 'icons/user-profile.svg';

@Component({
  selector        : 'wor-header',
  template: require('./header.component.html'),
  changeDetection : ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  public userProfileIcon   = this.sanitizer.bypassSecurityTrustHtml(userProfileIcon);

  warnings : Array<SessionWarningModel>;

  constructor (
    @Inject(forwardRef(() => CompanyService))
    public companyService               : CompanyService,
    @Inject(forwardRef(() => ApiService)) private apiService                  : ApiService,
    @Inject(forwardRef(() => BusinessIntelligenceService)) private businessIntelligenceService : BusinessIntelligenceService,
    @Inject(forwardRef(() => ConfirmService)) private confirmService              : ConfirmService,
    @Inject(forwardRef(() => MessagesService)) private messagesService             : MessagesService,
    private modalService                : NgbModal,
    @Inject(forwardRef(() => MyobService)) private myobService                 : MyobService,
    @Inject(forwardRef(() => PluralizePipe)) public pluralizePipe                : PluralizePipe,
    @Inject(forwardRef(() => QuickbooksService)) private quickbooksService           : QuickbooksService,
    private router                      : UIRouter,
    private sanitizer                   : DomSanitizer,
    @Inject(forwardRef(() => SessionService))
    public sessionService               : SessionService,
    @Inject(forwardRef(() => SidebarService)) private sidebarService              : SidebarService,
    private translateService            : TranslateService,
    @Inject(forwardRef(() => XeroService)) private xeroService                 : XeroService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit () : void {
    this.warnings = this.sessionService.getWarnings();

    // CompanyService properties settle after the first CD cycle;
    // force a second check to avoid ExpressionChangedAfterItHasBeenCheckedError.
    setTimeout(() => this.cdr.detectChanges());
  }

  create ( state = 'app' ) : void {
    this.router.stateService.go(state, undefined, {
      inherit: false
    });

    this.resetSidebar();
  }

  dismissWarning ( warning = { id: '*' }) : void {
    this.apiService.get('/company/dismiss_notification', warning.id)
    .then(( warnings : Array<SessionWarningModel> ) => {
      this.warnings = warnings;
    });
  }

  hasRights ( module : string ) : boolean {
    return this.sessionService.hasRights(module);
  }

  logout () : void {
    this.apiService.delete('/sessions')
    .finally(() => {
      this.businessIntelligenceService.logout();

      this.sessionService.clear();

      window.location.assign('login.html');
    });
  }

  resetSidebar () : void {
    this.sidebarService.reset();
  }

  syncMyob () {
    if (this.companyService.isLinkedToBranch() && this.companyService.isSharedAccounting() && !this.companyService.company.last_myob_sync_down) {
      this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.LINK_TOOL'), undefined, {
        cancelText : 'GENERAL_SPACE.BUTTON.CONTACTS_NO',
        confirmText: 'GENERAL_SPACE.BUTTON.CONTACTS_YES',
        size       : 'lg'
      }).then(() => {
        this.router.stateService.go('app.branches.contacts');
      })
      .catch(() => {
        this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.CONFIRM_SYNC'))
        .then(() => this.myobService.sync())
        .then(() => {
          this.messagesService.show(this.translateService.instant('JS_SPACE.MYOB.SYNC_CONFIRM'), 'success');
        })
        .catch(() => {
          this.apiService.get('myob/ignore_sync_before')
          .then(() => this.myobService.sync())
          .then(() => {
              this.messagesService.show(this.translateService.instant('JS_SPACE.MYOB.SYNC_CONFIRM'), 'success');
          });
        });
      });
    }
    else {
      return this.myobService.sync()
      .then(() => {
        this.messagesService.show(this.translateService.instant('JS_SPACE.MYOB.SYNC_CONFIRM'), 'success');
      });
    }
  }

  syncQuickbooks () {
    if (this.companyService.isLinkedToBranch() && this.companyService.isSharedAccounting() && !this.companyService.company.last_qbo_sync_down) {
      this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.LINK_TOOL'), undefined, {
        cancelText : 'GENERAL_SPACE.BUTTON.CONTACTS_NO',
        confirmText: 'GENERAL_SPACE.BUTTON.CONTACTS_YES',
        size       : 'lg'
      })
      .then(() => {
        this.router.stateService.go('app.branches.contacts');
      })
      .catch(() => {
        this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.CONFIRM_SYNC'))
        .then(() => this.quickbooksService.sync())
        .then(() => {
          this.messagesService.show(this.translateService.instant('JS_SPACE.QUICKBOOKS.SYNC_CONFIRM'), 'success');
        })
        .catch(() => {
          this.apiService.get('qbo/ignore_sync_before')
          .then(() => this.quickbooksService.sync())
          .then(() => {
            this.messagesService.show(this.translateService.instant('JS_SPACE.QUICKBOOKS.SYNC_CONFIRM'), 'success');
          });
        });
      });
    }
    else {
      this.quickbooksService.sync()
      .then(() => {
        this.messagesService.show(this.translateService.instant('JS_SPACE.QUICKBOOKS.SYNC_CONFIRM'), 'success');
      });
    }
  };

  syncXero () : void {
    if (this.companyService.isLinkedToBranch() && this.companyService.isSharedAccounting() && !this.companyService.company.last_xero_sync_down) {
      this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.LINK_TOOL'), undefined, {
        cancelText : 'GENERAL_SPACE.BUTTON.CONTACTS_NO',
        confirmText: 'GENERAL_SPACE.BUTTON.CONTACTS_YES',
        size       : 'lg'
      })
      .then(() => this.router.stateService.go('app.branches.contacts'))
      .catch(() => {
        this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.CONFIRM_SYNC'))
        .then(() => {
          this.xeroService.sync()
          .then(( conflicts : number ) => {
            if (conflicts) {
              this.router.stateService.go('app.xero-contacts');
            }
            else {
              this.messagesService.show(this.translateService.instant('JS_SPACE.XERO.SYNC_CONFIRM'), 'success');
            }
          });
        })
        .catch(() => {
          this.apiService.get('xero/ignore_sync_before')
          .then(() => {
            return this.xeroService.sync()
            .then(( conflicts : number ) => {
              if (conflicts) {
                this.router.stateService.go('app.xero-contacts');
              }
              else {
                this.messagesService.show(this.translateService.instant('JS_SPACE.XERO.SYNC_CONFIRM'), 'success');
              }
            });
          });
        });
      });
    }
    else {
      this.xeroService.sync()
      .then(( conflicts : number ) => {
        if (conflicts) {
          this.router.stateService.go('app.xero-contacts');
        }
        else {
          this.messagesService.show(this.translateService.instant('JS_SPACE.XERO.SYNC_CONFIRM'), 'success');
        }
      });
    }
  }
}
